import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const productDataCache = new Map();

function normalizeMediaKey(value) {
  if (!value) return '';

  try {
    const url = new URL(value, window.location.origin);
    const pathname = decodeURIComponent(url.pathname);
    return (pathname.split('/').filter(Boolean).pop() || '').toLowerCase();
  } catch {
    const clean = String(value).split('?')[0].split('#')[0];
    return decodeURIComponent(clean.split('/').filter(Boolean).pop() || '').toLowerCase();
  }
}

function slugifyOptionValue(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function getProductHandleFromPath() {
  const match = window.location.pathname.match(/\/products\/([^/?]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

async function getCurrentProductData() {
  const handle = getProductHandleFromPath();
  if (!handle) return null;

  if (!productDataCache.has(handle)) {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(handle)}.js`;

    productDataCache.set(
      handle,
      fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)
    );
  }

  return productDataCache.get(handle);
}

function getVariantImageUrl(variant) {
  const featured = variant?.featured_image;
  if (typeof featured === 'string') return featured;
  return featured?.src || featured?.url || variant?.image?.src || variant?.image?.url || '';
}

function getProductImageUrl(image) {
  if (typeof image === 'string') return image;
  return image?.src || image?.url || '';
}

function getColorIndex(product) {
  if (!Array.isArray(product?.options)) return -1;

  return product.options.findIndex((option) => {
    const name = typeof option === 'string' ? option : option?.name;
    const normalized = String(name || '').trim().toLowerCase();
    return normalized === 'color' || normalized === 'colour';
  });
}

function getColorGroupByFilename(product, variant) {
  const colorIndex = getColorIndex(product);
  if (colorIndex < 0 || !Array.isArray(variant?.options)) return null;

  const selectedColor = variant.options[colorIndex];
  const colorSlug = slugifyOptionValue(selectedColor);
  if (!colorSlug) return null;

  const prefixes = [`${colorSlug}_image_`, `${colorSlug}-image-`];
  const matches = product.images
    .map((image) => normalizeMediaKey(getProductImageUrl(image)))
    .filter((key) => key && prefixes.some((prefix) => key.startsWith(prefix)));

  return matches.length ? new Set(matches) : null;
}

function getColorGroupByAnchors(product, variant) {
  const anchors = new Set(
    product.variants
      .map((item) => normalizeMediaKey(getVariantImageUrl(item)))
      .filter(Boolean)
  );

  if (anchors.size < 2) return null;

  const selectedAnchor = normalizeMediaKey(getVariantImageUrl(variant));
  if (!selectedAnchor || !anchors.has(selectedAnchor)) return null;

  const orderedImages = product.images
    .map((image) => normalizeMediaKey(getProductImageUrl(image)))
    .filter(Boolean);

  const start = orderedImages.findIndex((image) => image === selectedAnchor);
  if (start < 0) return null;

  let end = orderedImages.length;
  for (let index = start + 1; index < orderedImages.length; index += 1) {
    if (anchors.has(orderedImages[index])) {
      end = index;
      break;
    }
  }

  const group = orderedImages.slice(start, end);
  return group.length ? new Set(group) : null;
}

function getColorGroup(product, variantId) {
  if (!product?.variants?.length || !product?.images?.length) return null;

  const variant = product.variants.find((item) => String(item.id) === String(variantId));
  if (!variant) return null;

  return getColorGroupByFilename(product, variant) || getColorGroupByAnchors(product, variant);
}

function elementUsesAllowedImage(element, allowedImages) {
  const images = element.querySelectorAll('img');

  for (const image of images) {
    const candidates = [image.currentSrc, image.src, image.getAttribute('src')];
    const srcset = image.getAttribute('srcset');

    if (srcset) {
      for (const part of srcset.split(',')) {
        candidates.push(part.trim().split(/\s+/)[0]);
      }
    }

    if (candidates.some((candidate) => allowedImages.has(normalizeMediaKey(candidate)))) {
      return true;
    }
  }

  return false;
}

function rewriteIndexedAction(element, attribute, action, index) {
  const value = element.getAttribute(attribute);
  if (!value || !value.includes(`/${action}/`)) return;

  element.setAttribute(
    attribute,
    value.replace(new RegExp(`/${action}/\\d+`), `/${action}/${index}`)
  );
}

function filterGalleryToColor(gallery, allowedImages) {
  if (!gallery || !allowedImages?.size) return false;

  const selectors = [
    'slideshow-slides > slideshow-slide',
    '.media-gallery__grid > li',
    '.dialog-zoomed-gallery > li',
    '.dialog-thumbnails-list__thumbnail',
    '.slideshow-controls__thumbnail',
  ];

  let keptSlides = [];

  for (const selector of selectors) {
    const nodes = Array.from(gallery.querySelectorAll(selector));

    for (const node of nodes) {
      if (!elementUsesAllowedImage(node, allowedImages)) node.remove();
    }

    if (selector === 'slideshow-slides > slideshow-slide') {
      keptSlides = Array.from(gallery.querySelectorAll(selector));
    }
  }

  if (!keptSlides.length) return false;

  keptSlides.forEach((slide, index) => {
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');

    const style = slide.getAttribute('style') || '';
    if (style.includes('--slideshow-timeline:')) {
      slide.setAttribute(
        'style',
        style.replace(
          /--slideshow-timeline:\s*--slide-\d+;/,
          `--slideshow-timeline: --slide-${index};`
        )
      );
    }

    for (const descendant of slide.querySelectorAll('*')) {
      rewriteIndexedAction(descendant, 'on:click', 'open', index);
    }
  });

  Array.from(gallery.querySelectorAll('.media-gallery__grid > li')).forEach((item, index) => {
    for (const descendant of item.querySelectorAll('*')) {
      rewriteIndexedAction(descendant, 'on:click', 'zoom', index);
    }
  });

  Array.from(gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')).forEach((button, index) => {
    rewriteIndexedAction(button, 'on:click', 'handleThumbnailClick', index);
    rewriteIndexedAction(button, 'on:pointerenter', 'handleThumbnailPointerEnter', index);
    if (index === 0) button.setAttribute('aria-selected', 'true');
    else button.removeAttribute('aria-selected');
  });

  Array.from(gallery.querySelectorAll('.slideshow-controls__thumbnail')).forEach((button, index) => {
    rewriteIndexedAction(button, 'on:click', 'select', index);
    if (index === 0) button.setAttribute('aria-selected', 'true');
    else button.removeAttribute('aria-selected');
  });

  const dots = Array.from(gallery.querySelectorAll('.slideshow-controls__dots > li'));
  dots.forEach((item, index) => {
    if (index >= keptSlides.length) {
      item.remove();
      return;
    }

    const button = item.querySelector('button');
    if (button) {
      rewriteIndexedAction(button, 'on:click', 'select', index);
      if (index === 0) button.setAttribute('aria-selected', 'true');
      else button.removeAttribute('aria-selected');
    }
  });

  for (const counter of gallery.querySelectorAll('.slideshow-controls__counter')) {
    const current = counter.querySelector('[ref="current"]');
    counter.innerHTML = '';

    const currentSpan = current || document.createElement('span');
    currentSpan.setAttribute('ref', 'current');
    currentSpan.textContent = '1';

    const slash = document.createElement('span');
    slash.className = 'slash';
    slash.textContent = '/';

    counter.append(currentSpan, slash, document.createTextNode(String(keptSlides.length)));
  }

  const component = gallery.querySelector('slideshow-component');
  if (component) {
    const style = component.getAttribute('style') || '';
    const timelines = Array.from(
      { length: keptSlides.length },
      (_, index) => `--slide-${index}`
    ).join(',');

    if (style.includes('--slideshow-timeline:')) {
      component.setAttribute(
        'style',
        style.replace(/--slideshow-timeline:[^;]*;/, `--slideshow-timeline: ${timelines};`)
      );
    }
  }

  gallery.dataset.colorGalleryFiltered = 'true';
  return true;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    if (!this.dataset.colorGalleryFiltered && getProductHandleFromPath()) {
      this.#filterInitialGallery();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  async #filterInitialGallery() {
    const product = await getCurrentProductData();
    if (!product) return;

    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    const formVariant = document.querySelector('product-form-component input[name="id"]')?.value;
    const variantId = urlVariant || formVariant || product.variants?.[0]?.id;
    const group = getColorGroup(product, variantId);
    if (!group) return;

    try {
      const response = await fetch(window.location.href, { credentials: 'same-origin' });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const gallery = html.querySelector('media-gallery');
      if (!gallery || !filterGalleryToColor(gallery, group)) return;

      this.replaceWith(gallery);
    } catch (error) {
      console.warn('[media-gallery] Initial color gallery filter failed:', error);
    }
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html, resource } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const product = await getCurrentProductData();
        const variantId = resource?.id;
        const group = product && variantId ? getColorGroup(product, variantId) : null;

        if (group) filterGalleryToColor(newMediaGallery, group);
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[media-gallery] Event promise rejected:', error);
        }
      });
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.slideshow?.select(index, undefined, { animate: false });
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
